import type { ComponentProps } from "react";

import { NumberField as DSNumberField, Field, type FieldClassNames } from "@eisox/design-system";

import { useFieldContext } from "../form.context";
import { FieldErrors } from "./field-errors.component";

type RootProps = Omit<ComponentProps<typeof DSNumberField.Root>, "value" | "onValueChange">;
type InputProps = ComponentProps<typeof DSNumberField.Input>;
type ButtonProps = ComponentProps<typeof DSNumberField.Decrement>;

interface NumberFieldProps extends RootProps {
  label?: string;
  description?: string;
  inputProps?: InputProps;
  decrementProps?: ButtonProps;
  incrementProps?: ButtonProps;
  classNames?: FieldClassNames;
  onValueChange?: (value: number | undefined) => void;
}

const NumberField: React.FC<NumberFieldProps> = ({
  label,
  description,
  inputProps,
  decrementProps,
  incrementProps,
  classNames,
  children,
  onValueChange,
  ...rootProps
}) => {
  const field = useFieldContext<number | undefined>();

  return (
    <Field.Root className={classNames?.root}>
      <Field.Label className={classNames?.label}>{label}</Field.Label>
      <Field.Description className={classNames?.description}>{description}</Field.Description>
      <Field.Control
        render={
          <DSNumberField.Root
            {...rootProps}
            value={field.state.value ?? null}
            onValueChange={(value, _eventDetails) => {
              field.handleChange(value ?? undefined);
              onValueChange?.(value ?? undefined);
            }}
            className={classNames?.control}
          >
            <DSNumberField.Group>
              <DSNumberField.Decrement {...decrementProps} />
              <DSNumberField.Input
                {...inputProps}
                onBlur={event => {
                  field.handleBlur();
                  inputProps?.onBlur?.(event);
                }}
              />
              {children}
              <DSNumberField.Increment {...incrementProps} />
            </DSNumberField.Group>
          </DSNumberField.Root>
        }
      />
      <FieldErrors className={classNames?.error} />
    </Field.Root>
  );
};

export { NumberField };
export type { NumberFieldProps };
